import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { TIDS } from "@/constants/testIds";
import { ArrowUpRight } from "lucide-react";
import Seo from "@/components/Seo";

export default function Login() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (user && user !== false) {
      nav(user.role === "admin" ? "/admin" : "/dashboard", { replace: true });
    }
  }, [user]); // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const u = await login(email, password);
      const to = loc.state?.from?.pathname || (u.role === "admin" ? "/admin" : "/dashboard");
      nav(to, { replace: true });
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[88vh] grid grid-cols-1 lg:grid-cols-2">
      <Seo title="Sign in" description="Sign in to the E-Quantum artist portal." path="/login" noindex />
      <div className="hidden lg:flex relative bg-[#0a0a0a] border-r border-white/10 p-12 flex-col justify-between dotted-grid">
        <div>
          <div className="font-heading font-black text-2xl tracking-tighter">
            E·QUANTUM<span className="text-[var(--primary)]">.</span>
          </div>
        </div>
        <div>
          <h2 className="font-heading font-black text-5xl tracking-tighter leading-[0.95] max-w-md">
            Welcome back to
            <br />
            <span className="text-[var(--primary)]">your empire.</span>
          </h2>
          <p className="mt-6 text-zinc-400 max-w-md leading-relaxed">
            Track your royalties, placements, publishing status and bookings —
            all in one place.
          </p>
        </div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-600">
          / artist portal
        </div>
      </div>

      <div className="flex items-center justify-center p-8 lg:p-16">
        <div className="w-full max-w-md">
          <div className="tag mb-4">// Sign in</div>
          <h1 className="font-heading font-black text-4xl md:text-5xl tracking-tighter mb-3">
            Sign in
          </h1>
          <p className="text-zinc-400 mb-8 text-sm">
            New here?{" "}
            <Link to="/register" className="text-[var(--primary)] hover:underline">
              Create an artist account
            </Link>
            .
          </p>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">
                Email
              </label>
              <input
                required
                type="email"
                data-testid={TIDS.loginEmail}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-gold"
                placeholder="you@artist.com"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-xs uppercase tracking-widest text-zinc-500">
                  Password
                </label>
                <Link
                  to="/forgot-password"
                  data-testid="login-forgot-link"
                  className="text-[10px] uppercase tracking-widest font-mono text-[var(--primary)] hover:text-white"
                >
                  Forgot?
                </Link>
              </div>
              <input
                required
                type="password"
                data-testid={TIDS.loginPassword}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-gold"
                placeholder="••••••••"
              />
            </div>

            {err && (
              <div
                data-testid={TIDS.loginError}
                className="border border-red-500/40 bg-red-500/10 text-red-300 text-sm px-4 py-3"
              >
                {err}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid={TIDS.loginSubmit}
              className="btn-primary w-full justify-center"
            >
              {loading ? "Signing in…" : "Sign in"} <ArrowUpRight size={16} />
            </button>
          </form>

          <div className="mt-8 p-4 border border-white/10 bg-[#0a0a0a]">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--primary)] mb-2">
              Demo accounts
            </div>
            <div className="text-xs text-zinc-400 space-y-1 font-mono">
              <div>Demo Artist: demo.artist@equantum.com / DemoArtist@2026</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
